// app/compare/online-vs-frontal.tsx — קורס אונליין vs פרונטלי | Kursi
import Head from 'expo-router/head';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Pressable,
  Platform,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Home, MessageCircle, CheckCircle2, XCircle } from 'lucide-react-native';
import { theme } from '@/constants/theme';
 
export default function OnlineVsFrontalPage() {
  const router = useRouter();
 
  const breadcrumb = Platform.OS === 'web'
    ? {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'דף הבית', item: 'https://kursiaii.com/' },
          { '@type': 'ListItem', position: 2, name: 'קורס אונליין vs פרונטלי', item: 'https://kursiaii.com/compare/online-vs-frontal' },
        ],
      }
    : null;
 
  const faqSchema = Platform.OS === 'web'
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'האם קורס אונליין שווה כמו קורס פרונטלי?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'זה תלוי בסוג הלמידה שלכם. אם אתם יכולים לנהל זמן בצורה עצמאית וממוקדת — קורס אונליין יכול להיות מצוין, לרוב בעלות נמוכה יותר. אם אתם זקוקים למבנה ואחריות קבוצתית — פרונטלי מוביל לתוצאות טובות יותר.',
            },
          },
          {
            '@type': 'Question',
            name: 'מה זול יותר — קורס אונליין או פרונטלי?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'קורסים אונליין זולים יותר בממוצע ב-30–50%. קורס Full Stack פרונטלי עולה 16,000–22,000 ש"ח; אותו קורס אונליין — 8,000–14,000 ש"ח. עם זאת, מנטורינג אישי בקורס פרונטלי יכול להיות שווה את ההפרש.',
            },
          },
          {
            '@type': 'Question',
            name: 'מי יותר מועסק — בוגרי אונליין או פרונטלי?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'לא נמצא פער משמעותי בהשמה. מה שמשפיע יותר: איכות הקורס הספציפי, פרויקטים שביצעתם ויכולת ראיונות. בוגרי קורסים פרונטליים נהנים לרוב מרשת קשרים קבוצתית שעוזרת לחיפוש עבודה.',
            },
          },
        ],
      }
    : null;
 
  return (
    <>
      {Platform.OS === 'web' && (
        <Head>
          <title>קורס אונליין vs פרונטלי 2026 — מה עדיף? | Kursi</title>
          <meta
            name="description"
            content="השוואה מלאה בין קורס אונליין לקורס פרונטלי בישראל 2026: מחיר, השמה, ניהול זמן, מנטורינג. עם טבלת השוואה, יתרונות וחסרונות וסיכום מומלץ."
          />
          <link rel="canonical" href="https://kursiaii.com/compare/online-vs-frontal" />
          <meta property="og:url" content="https://kursiaii.com/compare/online-vs-frontal" />
          <meta property="og:title" content="קורס אונליין vs פרונטלי 2026 | Kursi" />
          <meta property="og:description" content="מה עדיף — קורס אונליין או פרונטלי? השוואה מפורטת עם מחירים, אחוזי השמה וסיכום לפי סוג לומד." />
          <meta name="robots" content="index,follow" />
          {breadcrumb && (
            <script type="application/ld+json"
              // @ts-ignore
              dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />
          )}
          {faqSchema && (
            <script type="application/ld+json"
              // @ts-ignore
              dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
          )}
        </Head>
      )}
 
      <ScrollView style={styles.container} contentContainerStyle={styles.content} showsVerticalScrollIndicator>
        <View style={styles.navBar}>
          <Pressable style={({ pressed }) => [styles.navBtn, pressed && styles.navBtnPressed]}
            onPress={() => router.push('/chat')} accessibilityRole="button">
            <MessageCircle size={18} color={theme.colors.primary} strokeWidth={2} />
            <Text style={styles.navBtnText}>חיפוש חכם</Text>
          </Pressable>
          <Pressable style={({ pressed }) => [styles.navBtn, styles.navBtnHome, pressed && styles.navBtnPressed]}
            onPress={() => router.push('/')} accessibilityRole="button">
            <Home size={18} color="#7A6F5F" strokeWidth={2} />
            <Text style={[styles.navBtnText, styles.navBtnHomeText]}>דף הבית</Text>
          </Pressable>
        </View>
 
        <View style={styles.innerContent}>
          <Text style={styles.h1}>קורס אונליין vs פרונטלי 2026 — מה עדיף לכם?</Text>
          <Text style={styles.intro}>
            השאלה "אונליין או פרונטלי?" היא אחת הנפוצות שאנחנו מקבלים ב-Kursi. התשובה הקצרה: תלוי בכם.
            התשובה הארוכה — הנה.
          </Text>
 
          <View style={styles.cardsRow}>
            <View style={[styles.summaryCard, { backgroundColor: '#EDE9FE' }]}>
              <Text style={styles.cardTitle}>🖥️ אונליין</Text>
              <Text style={styles.cardSub}>גמישות מרבית</Text>
              <Text style={styles.cardSub}>עלות נמוכה ב-30–50%</Text>
              <Text style={styles.cardSub}>מתאים ללומדים עצמאיים</Text>
            </View>
            <View style={[styles.summaryCard, { backgroundColor: '#FEF3C7' }]}>
              <Text style={styles.cardTitle}>🏫 פרונטלי</Text>
              <Text style={styles.cardSub}>מבנה ואחריות</Text>
              <Text style={styles.cardSub}>מנטורינג אישי</Text>
              <Text style={styles.cardSub}>רשת קשרים קבוצתית</Text>
            </View>
          </View>
 
          <Text style={styles.sectionTitle}>טבלת השוואה מלאה</Text>
          <View style={styles.table}>
            {[
              { param: 'מחיר ממוצע', online: '8,000–14,000 ₪', frontal: '14,000–22,000 ₪' },
              { param: 'גמישות שעות', online: 'מלאה', frontal: 'מוגבלת (לו"ז קבוע)' },
              { param: 'גודל כיתה', online: '20–200 תלמידים', frontal: '8–25 תלמידים' },
              { param: 'מנטורינג אישי', online: 'מוגבל (forum, Slack)', frontal: 'גבוה (אינטראקציה ישירה)' },
              { param: 'אחוזי השלמה', online: '40–65%', frontal: '75–90%' },
              { param: 'רשת קשרים', online: 'ירוד (חבריי לכיתה וירטואליים)', frontal: 'גבוה (קשרים אישיים)' },
              { param: 'פרויקטים', online: 'לרוב עצמאיים', frontal: 'לרוב בקבוצות' },
              { param: 'תמיכה מיידית', online: 'דרך chat/forum', frontal: 'פנים אל פנים' },
              { param: 'עבודה בזמן לימוד', online: 'קל יותר', frontal: 'קשה יותר (שעות קבועות)' },
            ].map((row, i) => (
              <View key={i} style={[styles.tableRow, i % 2 === 0 && styles.tableRowEven]}>
                <Text style={styles.tableParam}>{row.param}</Text>
                <Text style={styles.tableCell}>{row.online}</Text>
                <Text style={styles.tableCell}>{row.frontal}</Text>
              </View>
            ))}
          </View>
 
          <View style={styles.twoColSection}>
            <View style={styles.col}>
              <Text style={styles.colTitle}>אונליין מתאים למי ש...</Text>
              {[
                'יכולים לנהל זמן עצמאית',
                'עובדים ורוצים ללמוד בזמן פנוי',
                'גרים רחוק ממרכז',
                'לומדים בקצב עצמם',
                'מחפשים חיסכון בתקציב',
              ].map((p, i) => (
                <View key={i} style={styles.bulletRow}>
                  <CheckCircle2 size={15} color={theme.colors.primary} />
                  <Text style={styles.bulletText}>{p}</Text>
                </View>
              ))}
            </View>
            <View style={styles.col}>
              <Text style={styles.colTitle}>פרונטלי מתאים למי ש...</Text>
              {[
                'זקוקים למבנה ואחריות',
                'לומדים טוב יותר מהסבר ישיר',
                'רוצים לבנות רשת קשרים',
                'עוברים הסבה מקצועית מהירה',
                'לא מפחדים מהשקעה גדולה יותר',
              ].map((p, i) => (
                <View key={i} style={styles.bulletRow}>
                  <CheckCircle2 size={15} color="#F59E0B" />
                  <Text style={styles.bulletText}>{p}</Text>
                </View>
              ))}
            </View>
          </View>
 
          <Text style={styles.sectionTitle}>⚠️ דגלים אדומים לפני הרישום</Text>
          {[
            'קורס אונליין ללא גישה למנטור — לאחר 3 חודשים תפסיקו לרוב.',
            'קורס פרונטלי עם 30+ תלמידים — המנטורינג יורד דרמטית.',
            'קורס שמבטיח "100% אונליין + עבודה מובטחת" — בדקו את האותיות הקטנות.',
            'קורס פרונטלי שעובד רק ב"לייב" ואין הקלטות — בעיה אם פספסתם שיעור.',
          ].map((flag, i) => (
            <View key={i} style={styles.redFlagRow}>
              <XCircle size={15} color="#EF4444" />
              <Text style={styles.redFlagText}>{flag}</Text>
            </View>
          ))}
 
          <Text style={styles.sectionTitle}>המלצת Kursi</Text>
          <Text style={styles.bodyText}>
            לרוב המסבים מקצוע בפעם הראשונה — <Text style={styles.bold}>פרונטלי עדיף</Text>. המבנה והמנטורינג
            מפצים על מה שקשה לשמור עצמאית בתחילת הדרך. לאנשים עם ניסיון לימוד עצמי ויכולת ניהול
            זמן גבוהה — אונליין יכול להיות הבחירה הטובה ביותר.
          </Text>
          <Text style={styles.bodyText}>
            הייעוץ שלנו: בחרו לפי <Text style={styles.bold}>האישיות שלכם</Text>, לא לפי המחיר.
            קורס זול שלא תסיימו — יקר יותר מקורס יקר שכן תסיימו.
          </Text>
 
          <View style={styles.ctaBlock}>
            <Text style={styles.ctaTitle}>רוצים לדעת מה הכי מתאים לכם?</Text>
            <Text style={styles.ctaBody}>ה-AI של Kursi ישאל אתכם 3 שאלות ויחזיר המלצה ספציפית — אונליין או פרונטלי, ובאיזה מוסד.</Text>
            <Pressable style={({ pressed }) => [styles.ctaBtn, pressed && { opacity: 0.85 }]}
              onPress={() => router.push('/chat')} accessibilityRole="button">
              <Text style={styles.ctaBtnText}>מצאו קורס עכשיו</Text>
            </Pressable>
          </View>
 
          <View style={styles.linksBlock}>
            <Text style={styles.linksTitle}>קישורים רלוונטיים</Text>
            {[
              { label: 'Full Stack vs Data Science', route: '/compare/fullstack-vs-data' },
              { label: 'קורסי סייבר vs QA', route: '/compare/cyber-vs-qa' },
              { label: 'שאלות נפוצות על קורסים', route: '/faq' },
              { label: 'קטלוג כל הקורסים', route: '/catalog' },
            ].map((link) => (
              <Pressable key={link.route}
                style={({ pressed }) => [styles.linkItem, pressed && { opacity: 0.75 }]}
                onPress={() => router.push(link.route as any)} accessibilityRole="link">
                <Text style={styles.linkText}>{link.label} →</Text>
              </Pressable>
            ))}
          </View>
        </View>
      </ScrollView>
    </>
  );
}
 
const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F2EBDD' },
  content: { paddingBottom: 48 },
  innerContent: { maxWidth: 860, alignSelf: 'center', width: '100%', paddingHorizontal: 16 },
  navBar: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingHorizontal: 16, paddingVertical: 10, backgroundColor: '#FAF5EA',
    borderBottomWidth: 1, borderBottomColor: '#1A1611', gap: 10,
  },
  navBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    paddingVertical: 8, paddingHorizontal: 14, borderRadius: 0, backgroundColor: '#FAF5EA',
  },
  navBtnHome: { backgroundColor: '#FAF5EA' },
  navBtnPressed: { opacity: 0.7 },
  navBtnText: { fontSize: 14, fontFamily: 'Heebo-Medium', color: theme.colors.primary },
  navBtnHomeText: { color: '#7A6F5F' },
  h1: {
    fontSize: 26, fontFamily: 'Heebo-Bold', color: '#1A1611',
    textAlign: 'right', marginTop: 24, marginBottom: 12, lineHeight: 38,
  },
  intro: { fontSize: 15, fontFamily: 'Heebo-Regular', color: '#3A332A', lineHeight: 26, textAlign: 'right', marginBottom: 24 },
  cardsRow: { flexDirection: 'row-reverse', gap: 12, marginBottom: 28 },
  summaryCard: {
    flex: 1, borderRadius: 0, padding: 16, gap: 6,
    ...(Platform.OS === 'web' ? ({ boxShadow: '0 4px 12px rgba(0,0,0,0.07)' } as any) : {}),
  },
  cardTitle: { fontSize: 16, fontFamily: 'Heebo-Bold', color: '#1A1611', textAlign: 'center', marginBottom: 4 },
  cardSub: { fontSize: 13, fontFamily: 'Heebo-Regular', color: '#3A332A', textAlign: 'center' },
  sectionTitle: { fontSize: 20, fontFamily: 'Heebo-Bold', color: '#1A1611', textAlign: 'right', marginTop: 28, marginBottom: 14 },
  table: { borderRadius: 0, overflow: 'hidden', borderWidth: 1, borderColor: '#1A1611', marginBottom: 24 },
  tableRow: { flexDirection: 'row-reverse', paddingVertical: 10, paddingHorizontal: 12 },
  tableRowEven: { backgroundColor: '#FAF5EA' },
  tableParam: { flex: 1.2, fontSize: 13, fontFamily: 'Heebo-Bold', color: '#1A1611', textAlign: 'right' },
  tableCell: { flex: 1, fontSize: 13, fontFamily: 'Heebo-Regular', color: '#3A332A', textAlign: 'center' },
  twoColSection: { flexDirection: 'row-reverse', gap: 16, marginBottom: 24 },
  col: { flex: 1 },
  colTitle: { fontSize: 15, fontFamily: 'Heebo-Bold', color: '#1A1611', textAlign: 'right', marginBottom: 10 },
  bulletRow: { flexDirection: 'row-reverse', gap: 8, marginBottom: 8, alignItems: 'flex-start' },
  bulletText: { flex: 1, fontSize: 13, fontFamily: 'Heebo-Regular', color: '#3A332A', textAlign: 'right', lineHeight: 20 },
  redFlagRow: { flexDirection: 'row-reverse', gap: 8, marginBottom: 8, alignItems: 'flex-start' },
  redFlagText: { flex: 1, fontSize: 14, fontFamily: 'Heebo-Regular', color: '#3A332A', textAlign: 'right', lineHeight: 22 },
  bodyText: { fontSize: 15, fontFamily: 'Heebo-Regular', color: '#3A332A', lineHeight: 26, textAlign: 'right', marginBottom: 12 },
  bold: { fontFamily: 'Heebo-Bold', color: '#1A1611' } as any,
  ctaBlock: { backgroundColor: '#FAF5EA', borderRadius: 0, padding: 24, alignItems: 'center', marginTop: 28, marginBottom: 28 },
  ctaTitle: { fontSize: 18, fontFamily: 'Heebo-Bold', color: '#1A1611', textAlign: 'center', marginBottom: 8 },
  ctaBody: { fontSize: 14, fontFamily: 'Heebo-Regular', color: '#3A332A', textAlign: 'center', marginBottom: 16, lineHeight: 22 },
  ctaBtn: { backgroundColor: theme.colors.primary, paddingVertical: 12, paddingHorizontal: 28, borderRadius: 0 },
  ctaBtnText: { fontSize: 15, fontFamily: 'Heebo-Bold', color: '#FAF5EA' },
  linksBlock: { marginBottom: 16 },
  linksTitle: { fontSize: 17, fontFamily: 'Heebo-Bold', color: '#1A1611', textAlign: 'right', marginBottom: 12 },
  linkItem: {
    paddingVertical: 10, paddingHorizontal: 14, backgroundColor: '#FAF5EA',
    borderRadius: 0, borderWidth: 1, borderColor: '#1A1611', marginBottom: 8,
  },
  linkText: { fontSize: 14, fontFamily: 'Heebo-Medium', color: theme.colors.primary, textAlign: 'right' },
});